import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { 
  X, 
  ShieldCheck, 
  Award, 
  Heart, 
  ShoppingBag, 
  Truck, 
  Check, 
  Share2, 
  Sparkles, 
  MessageCircle,
  Copy,
  Minus,
  Plus,
  Gem,
  Layers,
  ChevronRight,
  Maximize2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Play,
  Video,
  Zap
} from 'lucide-react';
import { formatINR, getPriceBreakup } from '../data/products';
import { resolveProductImage, handleProductImageError } from '../utils/imageHelper';

export default function QuickViewModal() {
  const navigate = useNavigate();
  const { 
    quickViewProduct, 
    setQuickViewProduct, 
    addToCart, 
    wishlist, 
    toggleWishlist,
    addToast,
    products,
    masterMetalRates,
    calculateProductPrice,
    getMasterRateForProduct,
    storeSettings
  } = useShop();

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [qty, setQty] = useState(1);
  const [isBreakupOpen, setIsBreakupOpen] = useState(true);
  const [copiedSku, setCopiedSku] = useState(false);
  const [selectedSize, setSelectedSize] = useState('7');
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imageFitMode, setImageFitMode] = useState('contain'); // default contain so no jewellery is cropped!
  const [isVideoActive, setIsVideoActive] = useState(false);

  useEffect(() => {
    if (quickViewProduct) {
      setActiveImageIndex(0);
      setIsVideoActive(false);
      setQty(1);
      setIsBreakupOpen(true);
      setCopiedSku(false);
      setSelectedSize(quickViewProduct.size || '7');
      setIsLightboxOpen(false);
      setZoomLevel(1);
      setImageFitMode('contain');
    }
  }, [quickViewProduct]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isLightboxOpen) {
          setIsLightboxOpen(false);
          setZoomLevel(1);
        } else if (quickViewProduct) {
          setQuickViewProduct(null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, quickViewProduct, setQuickViewProduct]);

  if (!quickViewProduct) return null;

  const pricing = calculateProductPrice ? calculateProductPrice(quickViewProduct, masterMetalRates) : null;
  const isFavorited = Array.isArray(wishlist) && wishlist.includes(quickViewProduct.id);
  const livePrice = pricing?.finalPrice || Number(quickViewProduct.price) || 0;
  const rawWeight = pricing?.weight || parseFloat(quickViewProduct.netGoldWeight || quickViewProduct.goldWeight || quickViewProduct.weight || quickViewProduct.grossWeight || 1.0) || 1.0;
  
  // Guarantee ratePerGram is always resolved and never 0
  const ratePerGram = (pricing?.ratePerGram && pricing.ratePerGram > 0) 
    ? pricing.ratePerGram 
    : (getMasterRateForProduct ? getMasterRateForProduct(quickViewProduct, masterMetalRates) : 6765);
    
  const goldAmount = (pricing?.metalValue !== undefined && pricing.metalValue > 0) 
    ? pricing.metalValue 
    : Math.round(rawWeight * ratePerGram);

  const diamondAmount = pricing?.diamondAmount || 0;
  const gemstoneAmount = pricing?.gemstoneAmount || 0;
    
  const makingChargePct = pricing?.makingChargePercent !== undefined ? pricing.makingChargePercent : 6.99;
  const makingChargesDiscounted = (pricing?.makingCharge !== undefined && pricing.makingCharge > 0) 
    ? pricing.makingCharge 
    : Math.round(goldAmount * (makingChargePct / 100));
    
  const taxableAmount = pricing?.taxableAmount || (goldAmount + diamondAmount + gemstoneAmount + makingChargesDiscounted);
  const gstAmount = pricing?.gstAmount || Math.round(taxableAmount * 0.03);

  const breakup = getPriceBreakup({
    ...quickViewProduct,
    ...pricing,
    price: livePrice,
    metalValue: goldAmount,
    goldAmount: goldAmount,
    diamondAmount,
    gemstoneAmount,
    makingCharge: makingChargesDiscounted,
    makingChargesDiscounted: makingChargesDiscounted,
    makingChargePercent: makingChargePct,
    taxableAmount,
    gstAmount
  });

  // Derive specs safely
  const safeId = String(quickViewProduct.id || '');
  const sku = quickViewProduct.sku || `ML-${(quickViewProduct.category || 'JW').toUpperCase().slice(0, 2)}-${safeId.replace(/[^0-9]/g, '') || '10554'}`;
  const origin = quickViewProduct.origin || 'India';
  const sizes = quickViewProduct.sizes || (
    quickViewProduct.category === 'rings' ? ['6', '7', '8', '9', '10', '12'] :
    quickViewProduct.category === 'bangles' ? ['2.4', '2.6', '2.8'] :
    quickViewProduct.category === 'necklaces' ? ['16 Inch', '18 Inch', 'Choker'] :
    ['Standard']
  );

  const isSilver = quickViewProduct.category === 'silverware' || 
                  quickViewProduct.category === 'kitchenware' || 
                  (quickViewProduct.metal && String(quickViewProduct.metal).toLowerCase().includes('silver'));

  const goldPurity = quickViewProduct.goldPurity || (
    isSilver ? '925 Sterling Silver' :
    String(quickViewProduct.purity || '').includes('916') ? '22KT' :
    String(quickViewProduct.purity || '').includes('750') ? '18KT' :
    String(quickViewProduct.purity || '').includes('585') ? '14KT' :
    '22KT'
  );

  const goldColor = quickViewProduct.goldColor || (
    isSilver ? 'Antique Silver' :
    String(quickViewProduct.metal || '').includes('White') ? 'White' :
    String(quickViewProduct.metal || '').includes('Rose') ? 'Rose' :
    'Yellow'
  );

  const goldWeight = quickViewProduct.goldWeight || (
    quickViewProduct.netGoldWeight ? String(quickViewProduct.netGoldWeight).replace(/grams?/i, 'G').trim() :
    quickViewProduct.grossWeight ? String(quickViewProduct.grossWeight).replace(/grams?/i, 'G').trim() :
    '1.000 G'
  );

  const hasDiamond = Boolean(breakup.diamondAmount > 0 || (quickViewProduct.diamondCarat && !String(quickViewProduct.diamondCarat).includes('N/A')));
  const diamondWeight = quickViewProduct.diamondWeight || (breakup.diamondWeight || '0.094 Ct');
  const diamondPieces = quickViewProduct.diamondPieces || breakup.diamondPieces || 9;
  const diamondClarity = quickViewProduct.diamondClarity || breakup.diamondClarity || 'SI-HI';

  // Fallback images
  const rawProductImages = Array.isArray(quickViewProduct.images) && quickViewProduct.images.length > 0
    ? quickViewProduct.images
    : [quickViewProduct.image || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=800&q=80'];
  const productImages = rawProductImages.map(img => resolveProductImage(img, quickViewProduct));

  // Related products for "YOU MAY ALSO LIKE"
  const relatedProducts = (Array.isArray(products) ? products : [])
    .filter(p => p && p.id !== quickViewProduct.id)
    .slice(0, 4);

  const handleCopySku = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(sku);
      setCopiedSku(true);
      addToast(`SKU ${sku} copied to clipboard!`, 'success');
      setTimeout(() => setCopiedSku(false), 2500);
    }
  };

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      addToast('Product link copied to clipboard!', 'info');
    }
  };

  const handleWhatsAppInquiry = () => {
    const text = encodeURIComponent(
      `Hello M.L. Jewellers, I am inquiring about "${quickViewProduct.name}" (SKU: ${sku}, Size: ${selectedSize}, Price: ${formatINR(quickViewProduct.price)}). Please provide more details.`
    );
    window.open(`https://api.whatsapp.com/send?phone=919876543210&text=${text}`, '_blank');
  };

  return (
    <div 
      className={`modal-overlay ${quickViewProduct ? 'active' : ''}`}
      onClick={() => setQuickViewProduct(null)}
      style={{ zIndex: 1200, padding: '1rem' }}
    >
      <div 
        className="modal-content kisna-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '920px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '16px',
          background: '#0d0d14',
          border: '1px solid rgba(212, 175, 55, 0.28)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.85), 0 0 30px rgba(212, 175, 55, 0.12)'
        }}
      >
        {/* Top Header Bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.85rem 1.4rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          background: 'rgba(18, 18, 28, 0.95)',
          backdropFilter: 'blur(10px)',
          position: 'sticky',
          top: 0,
          zIndex: 20
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <span style={{
              fontFamily: 'var(--font-serif)',
              fontWeight: 800,
              letterSpacing: '0.15em',
              fontSize: '1.05rem',
              color: 'var(--gold-400)',
              textTransform: 'uppercase'
            }}>
              M.L. JEWELLERS
            </span>
            <span style={{
              fontSize: '0.65rem',
              padding: '0.15rem 0.5rem',
              borderRadius: '999px',
              background: 'rgba(212, 175, 55, 0.15)',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              color: 'var(--gold-300)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              BIS 916 & Certified
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={() => toggleWishlist(quickViewProduct.id)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'var(--transition)'
              }}
              title={isFavorited ? 'Remove from Wishlist' : 'Add to Wishlist'}
            >
              <Heart
                size={18}
                color={isFavorited ? '#f43f5e' : '#fff'}
                fill={isFavorited ? '#f43f5e' : 'none'}
              />
            </button>

            <button
              onClick={() => setQuickViewProduct(null)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'var(--transition)'
              }}
              title="Close View"
            >
              <X size={19} />
            </button>
          </div>
        </div>

        {/* Scrollable Body Content */}
        <div style={{
          overflowY: 'auto',
          flex: 1,
          padding: '1.2rem',
          paddingBottom: '5rem' // leave room for sticky bottom action bar
        }} className="kisna-scroll-content">

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.15fr',
            gap: '1.8rem',
            alignItems: 'start'
          }} className="quickview-grid">

            {/* Left Column: Image Showcase & Thumbnails */}
            <div style={{ position: 'sticky', top: '0' }} className="quickview-gallery-col">
              {/* Main Image Frame */}
              <div 
                style={{
                  borderRadius: '14px',
                  overflow: 'hidden',
                  height: '380px',
                  backgroundColor: '#07070b',
                  background: 'radial-gradient(circle at center, #181826 0%, #07070b 100%)',
                  marginBottom: '0.85rem',
                  border: '1px solid rgba(212, 175, 55, 0.25)',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isVideoActive ? 'default' : 'zoom-in',
                  boxShadow: 'inset 0 0 25px rgba(0,0,0,0.85)'
                }}
                onClick={() => !isVideoActive && setIsLightboxOpen(true)}
                title={isVideoActive ? 'Product Video Reel' : 'Pura photo dekhne aur zoom karne ke liye click karein'}
              >
                {isVideoActive && quickViewProduct.videoUrl ? (
                  <div style={{ width: '100%', height: '100%', position: 'relative', background: '#000' }}>
                    <video
                      src={quickViewProduct.videoUrl}
                      controls
                      autoPlay
                      loop
                      playsInline
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      background: 'rgba(0,0,0,0.7)',
                      backdropFilter: 'blur(6px)',
                      border: '1px solid var(--border-gold)',
                      color: 'var(--gold-400)',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      padding: '0.2rem 0.55rem',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      pointerEvents: 'none'
                    }}>
                      <Video size={12} /> Product Video Reel
                    </div>
                  </div>
                ) : (
                  <>
                    <img
                      src={productImages[activeImageIndex] || productImages[0]}
                      alt={quickViewProduct.name || 'Jewellery'}
                      onError={(e) => handleProductImageError(e, quickViewProduct)}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: imageFitMode === 'contain' ? 'auto' : '100%',
                        height: imageFitMode === 'contain' ? 'auto' : '100%',
                        objectFit: imageFitMode,
                        padding: imageFitMode === 'contain' ? '8px' : '0',
                        transition: 'transform 0.3s ease'
                      }}
                    />

                    {/* Floating Hallmarking Badge */}
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      display: 'flex',
                      gap: '6px',
                      zIndex: 3
                    }}>
                      <span style={{
                        background: 'rgba(10, 10, 15, 0.85)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(212, 175, 55, 0.4)',
                        color: 'var(--gold-400)',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        padding: '0.2rem 0.55rem',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <ShieldCheck size={12} /> {quickViewProduct.purity || 'BIS Hallmarked'}
                      </span>
                    </div>

                    {/* Watch Video Reel Button (if product has video) */}
                    {quickViewProduct.videoUrl && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsVideoActive(true);
                        }}
                        style={{
                          position: 'absolute',
                          bottom: '10px',
                          left: '10px',
                          background: 'linear-gradient(135deg, #d4af37, #f3e5ab)',
                          color: '#0a0a0f',
                          border: 'none',
                          padding: '0.3rem 0.75rem',
                          borderRadius: '999px',
                          fontSize: '0.72rem',
                          fontWeight: 800,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          boxShadow: '0 4px 15px rgba(212,175,55,0.4)',
                          zIndex: 4
                        }}
                      >
                        <Play size={12} fill="#0a0a0f" /> Watch Video Reel
                      </button>
                    )}

                    {/* Bottom Overlay Controls: Fit/Fill Toggle & Full Screen Zoom */}
                    <div style={{
                      position: 'absolute',
                      bottom: '10px',
                      right: '10px',
                      display: 'flex',
                      gap: '6px',
                      zIndex: 3
                    }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageFitMode(prev => prev === 'contain' ? 'cover' : 'contain');
                        }}
                        style={{
                          background: 'rgba(15, 15, 25, 0.85)',
                          backdropFilter: 'blur(8px)',
                          border: '1px solid rgba(212, 175, 55, 0.3)',
                          color: 'var(--gold-300)',
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          padding: '0.25rem 0.55rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'var(--transition)'
                        }}
                        title={imageFitMode === 'contain' ? 'Click to Fill Frame' : 'Click to Fit Complete Photo'}
                      >
                        {imageFitMode === 'contain' ? '📐 Pura Photo' : '🔲 Fill'}
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsLightboxOpen(true);
                        }}
                        style={{
                          background: 'rgba(212, 175, 55, 0.22)',
                          backdropFilter: 'blur(8px)',
                          border: '1px solid rgba(212, 175, 55, 0.45)',
                          color: '#fff',
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          padding: '0.25rem 0.55rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                          transition: 'var(--transition)'
                        }}
                      >
                        <Maximize2 size={12} color="var(--gold-400)" /> Pura Kholein (Zoom)
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Multi-angle Thumbnails Strip (Main + 5 Angles + Video Reel) */}
              <div 
                className="hide-scrollbar"
                style={{ 
                  display: 'flex', 
                  gap: '0.5rem',
                  overflowX: 'auto',
                  paddingBottom: '6px'
                }}
              >
                {productImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setActiveImageIndex(idx);
                      setIsVideoActive(false);
                    }}
                    style={{
                      width: '60px',
                      height: '60px',
                      flexShrink: 0,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: (!isVideoActive && activeImageIndex === idx) ? '2px solid var(--gold-400)' : '1px solid rgba(255, 255, 255, 0.1)',
                      cursor: 'pointer',
                      padding: 0,
                      background: '#07070b',
                      position: 'relative',
                      opacity: (!isVideoActive && activeImageIndex === idx) ? 1 : 0.65,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <img
                      src={img}
                      alt={`Angle ${idx}`}
                      onError={(e) => handleProductImageError(e, quickViewProduct)}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    {idx === 0 && (
                      <span style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'var(--gold-500)',
                        color: '#000',
                        fontSize: '0.5rem',
                        fontWeight: 900,
                        textAlign: 'center',
                        lineHeight: '13px'
                      }}>
                        MAIN
                      </span>
                    )}
                  </button>
                ))}

                {/* Video Reel Thumbnail in Strip */}
                {quickViewProduct.videoUrl && (
                  <button
                    onClick={() => setIsVideoActive(true)}
                    style={{
                      width: '60px',
                      height: '60px',
                      flexShrink: 0,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: isVideoActive ? '2px solid var(--gold-400)' : '1px solid rgba(212, 175, 55, 0.4)',
                      cursor: 'pointer',
                      padding: 0,
                      background: 'linear-gradient(135deg, #181826 0%, #0c0c14 100%)',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: isVideoActive ? 1 : 0.75,
                      boxShadow: isVideoActive ? '0 0 12px rgba(212, 175, 55, 0.4)' : 'none',
                      transition: 'all 0.2s ease'
                    }}
                    title="Watch Video Reel"
                  >
                    <div style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #d4af37, #f59e0b)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '2px'
                    }}>
                      <Play size={10} fill="#0a0a0f" color="#0a0a0f" style={{ marginLeft: '1px' }} />
                    </div>
                    <span style={{ fontSize: '0.55rem', fontWeight: 800, color: 'var(--gold-400)', textTransform: 'uppercase' }}>
                      Video
                    </span>
                  </button>
                )}
              </div>

              {/* Trust Features Row Below Gallery */}
              <div style={{
                marginTop: '1.2rem',
                padding: '0.8rem',
                borderRadius: '10px',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.6rem',
                fontSize: '0.74rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--text-secondary)' }}>
                  <Truck size={14} color="var(--gold-400)" />
                  <span>Free Insured Transit</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--text-secondary)' }}>
                  <Award size={14} color="var(--gold-400)" />
                  <span>Lifetime Exchange</span>
                </div>
              </div>
            </div>

            {/* Right Column: Title, Kisna Specification Cards & Transparent Price Breakup */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              
              {/* Title & Subtitle */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                  <span className="badge badge-gold" style={{ fontSize: '0.68rem', padding: '0.2rem 0.5rem' }}>
                    <Sparkles size={11} /> {quickViewProduct.badge || 'Fine Masterpiece'}
                  </span>
                  {quickViewProduct.discount && (
                    <span className="badge badge-discount" style={{ fontSize: '0.68rem', padding: '0.2rem 0.5rem' }}>
                      {quickViewProduct.discount}
                    </span>
                  )}
                </div>

                <h1 style={{
                  fontSize: '1.35rem',
                  fontWeight: 700,
                  color: '#fff',
                  lineHeight: 1.3,
                  margin: '0.3rem 0 0.4rem 0'
                }}>
                  {quickViewProduct.name}
                </h1>

                <p style={{
                  fontSize: '0.82rem',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                  margin: 0
                }}>
                  {quickViewProduct.subtitle}
                </p>
              </div>

              {/* CARD 1: Product Details (Exact Kisna Layout) */}
              <div style={{
                borderRadius: '12px',
                border: '1px solid rgba(212, 175, 55, 0.22)',
                background: 'rgba(255, 255, 255, 0.03)',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '0.65rem 1rem',
                  background: 'rgba(212, 175, 55, 0.08)',
                  borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  color: 'var(--gold-300)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  Product Details
                </div>

                <div style={{ padding: '0.85rem 1rem', fontSize: '0.82rem' }}>
                  {/* SKU Row with Copy Icon */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '0.55rem'
                  }}>
                    <span style={{ width: '90px', color: 'var(--text-muted)' }}>SKU</span>
                    <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                    <span style={{ 
                      fontFamily: 'monospace', 
                      fontWeight: 700, 
                      color: '#fff', 
                      letterSpacing: '0.04em',
                      marginRight: '0.5rem' 
                    }}>
                      {sku}
                    </span>
                    <button
                      onClick={handleCopySku}
                      style={{
                        background: copiedSku ? 'rgba(52, 211, 153, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                        border: copiedSku ? '1px solid #34d399' : '1px solid rgba(255, 255, 255, 0.15)',
                        color: copiedSku ? '#34d399' : 'var(--gold-400)',
                        padding: '0.2rem 0.4rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        fontSize: '0.7rem',
                        transition: 'all 0.2s'
                      }}
                      title="Copy SKU code"
                    >
                      {copiedSku ? <Check size={12} /> : <Copy size={12} />}
                      <span>{copiedSku ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>

                  {/* Origin Row */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '0.55rem'
                  }}>
                    <span style={{ width: '90px', color: 'var(--text-muted)' }}>Origin</span>
                    <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{origin}</span>
                  </div>

                  {/* Size Row with Interactive Chips */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.4rem'
                  }}>
                    <span style={{ width: '90px', color: 'var(--text-muted)' }}>Size</span>
                    <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                    <span style={{ color: 'var(--gold-400)', fontWeight: 700, marginRight: '0.4rem' }}>
                      {selectedSize}
                    </span>

                    {/* Available size chips */}
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                      {sizes.map((s) => (
                        <button
                          key={s}
                          onClick={() => setSelectedSize(s)}
                          style={{
                            padding: '0.15rem 0.5rem',
                            fontSize: '0.72rem',
                            borderRadius: '4px',
                            background: selectedSize === s ? 'var(--gold-500)' : 'rgba(255, 255, 255, 0.06)',
                            color: selectedSize === s ? '#000' : '#e2e8f0',
                            border: selectedSize === s ? '1px solid var(--gold-400)' : '1px solid rgba(255, 255, 255, 0.1)',
                            cursor: 'pointer',
                            fontWeight: selectedSize === s ? 700 : 500
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* JewelCasa Style Extra Metadata Rows */}
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.55rem' }}>
                    <span style={{ width: '90px', color: 'var(--text-muted)' }}>Availability</span>
                    <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                    <span style={{ 
                      color: quickViewProduct?.inStock === false ? '#f87171' : '#34d399', 
                      fontWeight: 700, 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px' 
                    }}>
                      <span style={{ 
                        width: '6px', 
                        height: '6px', 
                        borderRadius: '50%', 
                        background: quickViewProduct?.inStock === false ? '#ef4444' : '#10b981' 
                      }} />
                      {quickViewProduct?.availability 
                        || (quickViewProduct?.inStock === false ? 'Out of Stock / Made to Order' : (storeSettings?.defaultAvailability || 'In Stock (Dispatch in 24-48 Hours)'))}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.55rem' }}>
                    <span style={{ width: '90px', color: 'var(--text-muted)' }}>Delivery</span>
                    <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>
                      {quickViewProduct?.delivery || storeSettings?.defaultDelivery || '3-5 Days (Insured Nationwide Transit)'}
                    </span>
                  </div>

                  {quickViewProduct?.category !== 'kitchenware' && (
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.55rem' }}>
                      <span style={{ width: '90px', color: 'var(--text-muted)' }}>Making Charges</span>
                      <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                      <span style={{ color: 'var(--gold-300)', fontWeight: 800 }}>
                        {pricing?.makingChargePercent ? `${pricing.makingChargePercent}%` : 'Direct Wholesale'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* CARD 2: Gold / Metal Details (Exact Kisna Layout) */}
              <div style={{
                borderRadius: '12px',
                border: '1px solid rgba(212, 175, 55, 0.22)',
                background: 'rgba(255, 255, 255, 0.03)',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '0.65rem 1rem',
                  background: 'rgba(212, 175, 55, 0.08)',
                  borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  color: 'var(--gold-300)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Layers size={15} color="var(--gold-400)" /> {isSilver ? 'Silver / Metal Details' : 'Gold Details'}
                </div>

                <div style={{ padding: '0.85rem 1rem', fontSize: '0.82rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.55rem' }}>
                    <span style={{ width: '100px', color: 'var(--text-muted)' }}>
                      {isSilver ? 'Silver Purity' : 'Gold Purity'}
                    </span>
                    <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{goldPurity}</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.55rem' }}>
                    <span style={{ width: '100px', color: 'var(--text-muted)' }}>
                      {isSilver ? 'Silver Rate' : 'Gold Rate'}
                    </span>
                    <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                    <span style={{ color: 'var(--gold-400)', fontWeight: 700 }}>
                      {pricing?.ratePerGram ? `${formatINR(pricing.ratePerGram)} / g` : '₹5,535 / g'}
                    </span>
                  </div>

                  {ratePerGram > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.55rem' }}>
                      <span style={{ width: '100px', color: 'var(--text-muted)' }}>
                        {isSilver ? 'Silver Rate' : 'Gold Rate'}
                      </span>
                      <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                      <span style={{ color: 'var(--gold-400)', fontWeight: 700 }}>
                        {formatINR(ratePerGram)} / g
                      </span>
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.55rem' }}>
                    <span style={{ width: '100px', color: 'var(--text-muted)' }}>
                      {isSilver ? 'Metal Finish' : 'Gold Color'}
                    </span>
                    <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{goldColor}</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ width: '100px', color: 'var(--text-muted)' }}>Weight</span>
                    <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                    <span style={{ color: 'var(--gold-400)', fontWeight: 700 }}>{goldWeight}</span>
                  </div>
                </div>
              </div>

              {/* CARD 3: Diamond Details (Exact Kisna Layout) */}
              {hasDiamond && (
                <div style={{
                  borderRadius: '12px',
                  border: '1px solid rgba(212, 175, 55, 0.22)',
                  background: 'rgba(255, 255, 255, 0.03)',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    padding: '0.65rem 1rem',
                    background: 'rgba(212, 175, 55, 0.08)',
                    borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
                    fontSize: '0.88rem',
                    fontWeight: 700,
                    color: 'var(--gold-300)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Gem size={15} color="#93c5fd" /> Diamond Details
                  </div>

                  <div style={{ padding: '0.85rem 1rem', fontSize: '0.82rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.55rem' }}>
                      <span style={{ width: '100px', color: 'var(--text-muted)' }}>Weight</span>
                      <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                      <span style={{ color: '#fff', fontWeight: 600 }}>{diamondWeight}</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.55rem' }}>
                      <span style={{ width: '100px', color: 'var(--text-muted)' }}>Pieces</span>
                      <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                      <span style={{ color: '#fff', fontWeight: 600 }}>{diamondPieces}</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ width: '100px', color: 'var(--text-muted)' }}>Clarity</span>
                      <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>:</span>
                      <span style={{ color: 'var(--gold-400)', fontWeight: 700 }}>{diamondClarity}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* CARD 4: PRICE BREAKUP (Collapsible Accordion matching Kisna) */}
              <div style={{
                borderRadius: '12px',
                border: '1px solid rgba(212, 175, 55, 0.35)',
                background: 'rgba(255, 255, 255, 0.04)',
                overflow: 'hidden',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)'
              }}>
                {/* Header with Minus / Plus toggle */}
                <button
                  onClick={() => setIsBreakupOpen(!isBreakupOpen)}
                  style={{
                    width: '100%',
                    padding: '0.8rem 1rem',
                    background: 'rgba(212, 175, 55, 0.12)',
                    border: 'none',
                    borderBottom: isBreakupOpen ? '1px solid rgba(212, 175, 55, 0.2)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    color: 'var(--gold-300)',
                    fontWeight: 800,
                    letterSpacing: '0.06em',
                    fontSize: '0.88rem'
                  }}
                >
                  <span>PRICE BREAKUP</span>
                  <span style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '6px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff'
                  }}>
                    {isBreakupOpen ? <Minus size={15} /> : <Plus size={15} />}
                  </span>
                </button>

                {/* Collapsible Content */}
                {isBreakupOpen && (
                  <div style={{ padding: '0.9rem 1rem', fontSize: '0.84rem' }}>
                    {/* Gold / Metal Row */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.65rem'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600, color: '#fff' }}>
                          {isSilver ? 'Silver' : 'Gold'}
                        </span>
                        {breakup.ratePerGram > 0 && (
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>
                            ({breakup.goldWeight} @ {formatINR(breakup.ratePerGram)}/g)
                          </span>
                        )}
                      </div>
                      <span style={{ fontWeight: 600, color: '#fff' }}>
                        {formatINR(breakup.goldAmount > 0 ? breakup.goldAmount : goldAmount)}
                      </span>
                    </div>

                    {/* Diamond / Gemstone Row (if present) */}
                    {hasDiamond && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '0.65rem'
                      }}>
                        <span style={{ fontWeight: 600, color: '#fff' }}>Diamond</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                          {breakup.diamondWeight || `${diamondWeight}`}
                        </span>
                        <span style={{ fontWeight: 600, color: '#fff' }}>
                          {formatINR(breakup.diamondAmount)}
                        </span>
                      </div>
                    )}

                    {/* Making Charges with Discount Tag */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.65rem'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600, color: '#fff' }}>Making Charges</span>
                        <span style={{ 
                          fontSize: '0.72rem', 
                          color: '#a78bfa', 
                          fontWeight: 600 
                        }}>
                          ({`${makingChargePct}% Special Rate`})
                        </span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        {breakup.makingChargesOriginal > (breakup.makingChargesDiscounted > 0 ? breakup.makingChargesDiscounted : makingChargesDiscounted) && (
                          <span style={{
                            color: 'var(--text-muted)',
                            textDecoration: 'line-through',
                            fontSize: '0.8rem'
                          }}>
                            {formatINR(breakup.makingChargesOriginal)}
                          </span>
                        )}
                        <span style={{ fontWeight: 600, color: '#fff' }}>
                          {formatINR(breakup.makingChargesDiscounted > 0 ? breakup.makingChargesDiscounted : makingChargesDiscounted)}
                        </span>
                      </div>
                    </div>

                    {/* GST(3%) Row */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.8rem',
                      paddingBottom: '0.8rem',
                      borderBottom: '1px dashed rgba(255, 255, 255, 0.15)'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600, color: '#fff' }}>GST (3%)</span>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          (Govt. Tax on {formatINR(breakup.taxableAmount)})
                        </span>
                      </div>
                      <span style={{ fontWeight: 600, color: '#34d399' }}>
                        + {formatINR(breakup.gstAmount)}
                      </span>
                    </div>

                    {/* Total Row with Strikethrough Original Total */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      paddingTop: '0.2rem'
                    }}>
                      <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>
                        Total
                      </span>

                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.7rem' }}>
                        {breakup.originalTotal > breakup.finalTotal && (
                          <span style={{
                            color: 'var(--text-muted)',
                            textDecoration: 'line-through',
                            fontSize: '0.88rem'
                          }}>
                            {formatINR(breakup.originalTotal)}
                          </span>
                        )}
                        <span style={{
                          fontWeight: 800,
                          fontSize: '1.18rem',
                          color: 'var(--gold-400)',
                          fontFamily: 'var(--font-serif)'
                        }}>
                          {formatINR(breakup.finalTotal)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Consultation & Share Row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: '0.4rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.08)'
              }}>
                <button
                  onClick={handleWhatsAppInquiry}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#25D366',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                    fontWeight: 700
                  }}
                >
                  <MessageCircle size={15} /> Inquire via WhatsApp
                </button>

                <button
                  onClick={handleShare}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    fontSize: '0.82rem',
                    cursor: 'pointer'
                  }}
                >
                  <Share2 size={14} /> Share
                </button>
              </div>

            </div>

          </div>

          {/* YOU MAY ALSO LIKE Section (Exact Kisna Layout) */}
          <div style={{ marginTop: '2.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              YOU MAY ALSO LIKE
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
              gap: '1rem'
            }}>
              {relatedProducts.map((prod) => (
                <div
                  key={prod.id}
                  onClick={() => {
                    setQuickViewProduct(prod);
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease'
                  }}
                  className="related-prod-card"
                >
                  <div style={{ height: '140px', overflow: 'hidden', backgroundColor: '#07070b' }}>
                    <img
                      src={prod.images[0]}
                      alt={prod.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                  <div style={{ padding: '0.65rem' }}>
                    <h4 style={{
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: '#fff',
                      margin: '0 0 0.3rem 0',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {prod.name}
                    </h4>
                    <div style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '0.4rem'
                    }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--gold-400)' }}>
                        {formatINR(prod.price)}
                      </span>
                      {prod.originalPrice && (
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textDecoration: 'line-through' }}>
                          {formatINR(prod.originalPrice)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* STICKY BOTTOM ACTION BAR (Exact Kisna Layout) */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(18, 18, 28, 0.98)',
          borderTop: '1px solid rgba(212, 175, 55, 0.25)',
          padding: '0.8rem 1.4rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 30,
          boxShadow: '0 -10px 25px rgba(0, 0, 0, 0.6)'
        }}>
          {/* Price & Tax Note on Left */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
              <span style={{
                fontSize: '1.35rem',
                fontWeight: 800,
                color: '#fff',
                fontFamily: 'var(--font-serif)',
                lineHeight: 1.1
              }}>
                {formatINR(livePrice)}
              </span>
              {quickViewProduct.originalPrice && (
                <span style={{
                  fontSize: '0.85rem',
                  color: 'var(--text-muted)',
                  textDecoration: 'line-through'
                }}>
                  {formatINR(Math.round(livePrice * 1.08))}
                </span>
              )}
            </div>
            <span style={{
              fontSize: '0.68rem',
              color: 'var(--text-secondary)',
              marginTop: '2px'
            }}>
              (inclusive of all taxes & 3% GST)
            </span>
          </div>

          {/* Actions on Right: Quantity Selector & Add to Cart Button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            {/* Quantity Controls */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              height: '42px'
            }}>
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  padding: '0 0.7rem',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                -
              </button>
              <span style={{ padding: '0 0.3rem', fontWeight: 700, fontSize: '0.88rem' }}>{qty}</span>
              <button
                onClick={() => setQty(qty + 1)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  padding: '0 0.7rem',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                +
              </button>
            </div>

            {/* Prominent Add to Cart Button */}
            <button
              onClick={() => {
                addToCart({
                  ...quickViewProduct,
                  price: livePrice,
                  selectedSize
                }, qty);
                setQuickViewProduct(null);
                addToast(`Added ${qty} × ${quickViewProduct.name} to Cart`, 'success');
              }}
              style={{
                background: 'linear-gradient(135deg, #2e1065 0%, #4c1d95 50%, #6b21a8 100%)',
                color: '#fff',
                border: '1px solid rgba(167, 139, 250, 0.4)',
                padding: '0 1.6rem',
                height: '44px',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '0.92rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 14px rgba(76, 29, 149, 0.45)',
                transition: 'all 0.2s ease'
              }}
              className="kisna-add-cart-btn"
            >
              <ShoppingBag size={17} /> Add to Cart
            </button>

            {/* Direct Instant Buy Now */}
            <button
              onClick={() => {
                addToCart({
                  ...quickViewProduct,
                  price: livePrice,
                  selectedSize
                }, qty);
                setQuickViewProduct(null);
                addToast(`Proceeding to checkout for ${quickViewProduct.name}!`, 'success');
                navigate('/checkout');
              }}
              style={{
                backgroundColor: '#fb641b',
                color: '#fff',
                border: 'none',
                padding: '0 1.6rem',
                height: '44px',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '0.92rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 14px rgba(251, 100, 27, 0.45)',
                textTransform: 'uppercase'
              }}
            >
              <Zap size={17} /> Buy Now
            </button>

            {/* Instant WhatsApp Inquiry (JewelCasa Style) */}
            <button
              onClick={() => {
                const phone = (storeSettings?.phone || storeSettings?.whatsapp || '919876543210').replace(/[^0-9]/g, '');
                const netWt = quickViewProduct.netGoldWeight || quickViewProduct.goldWeight || (pricing?.weight ? `${pricing.weight}g` : '1.8g');
                const purity = quickViewProduct.purity || quickViewProduct.goldPurity || '22K Hallmarked';
                const msg = `*💎 INQUIRY & QUOTE - ML JEWELLERS*\n` +
                  `• Product: ${quickViewProduct.name}\n` +
                  `• SKU: ${sku}\n` +
                  `• Net Weight: ${netWt}\n` +
                  `• Purity: ${purity}\n` +
                  `• Selected Size: ${selectedSize}\n` +
                  `• Locked Rate Price: ${formatINR(livePrice)}\n\n` +
                  `Please confirm booking and dispatch timeline.`;
                window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
                addToast('Connecting to WhatsApp with full product specs!', 'success');
              }}
              style={{
                background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                color: '#fff',
                border: 'none',
                padding: '0 1.2rem',
                height: '44px',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '0.88rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.45rem',
                boxShadow: '0 4px 14px rgba(37, 211, 102, 0.4)',
                transition: 'all 0.2s ease'
              }}
              title="Chat with Senior Jeweller on WhatsApp"
            >
              <MessageCircle size={17} /> WhatsApp Quote
            </button>
          </div>
        </div>

      </div>

      {/* FULLSCREEN LIGHTBOX & HD ZOOM MODAL */}
      {isLightboxOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            backgroundColor: 'rgba(5, 5, 10, 0.96)',
            backdropFilter: 'blur(20px)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '1rem',
            animation: 'fadeIn 0.2s ease forwards'
          }}
          onClick={() => {
            setIsLightboxOpen(false);
            setZoomLevel(1);
          }}
        >
          {/* Lightbox Header Bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.6rem 1rem',
              background: 'rgba(20, 20, 32, 0.8)',
              borderRadius: '12px',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              zIndex: 10
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h3 style={{ color: '#fff', fontSize: '1rem', fontWeight: 700, margin: 0 }}>
                {quickViewProduct.name}
              </h3>
              <p style={{ color: 'var(--gold-400)', fontSize: '0.75rem', margin: 0 }}>
                {quickViewProduct.purity || 'Hallmarked Jewellery'} • Photo {activeImageIndex + 1} of {productImages.length}
              </p>
            </div>

            {/* Zoom Controls & Close */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setZoomLevel(prev => Math.max(1, prev - 0.3))}
                disabled={zoomLevel <= 1}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                  cursor: zoomLevel <= 1 ? 'not-allowed' : 'pointer',
                  opacity: zoomLevel <= 1 ? 0.4 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Zoom Out"
              >
                <ZoomOut size={18} />
              </button>

              <button
                type="button"
                onClick={() => setZoomLevel(1)}
                style={{
                  padding: '0.3rem 0.6rem',
                  borderRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: 'var(--gold-300)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
                title="Reset Zoom"
              >
                {Math.round(zoomLevel * 100)}%
              </button>

              <button
                type="button"
                onClick={() => setZoomLevel(prev => Math.min(3, prev + 0.3))}
                disabled={zoomLevel >= 3}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                  cursor: zoomLevel >= 3 ? 'not-allowed' : 'pointer',
                  opacity: zoomLevel >= 3 ? 0.4 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Zoom In"
              >
                <ZoomIn size={18} />
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsLightboxOpen(false);
                  setZoomLevel(1);
                }}
                style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '50%',
                  background: 'rgba(220, 38, 38, 0.2)',
                  border: '1px solid rgba(220, 38, 38, 0.4)',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: '0.5rem'
                }}
                title="Close Fullscreen View (Esc)"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Lightbox Center Image Stage */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              position: 'relative',
              cursor: zoomLevel > 1 ? 'grab' : 'zoom-in',
              padding: '1rem'
            }}
            onClick={(e) => {
              e.stopPropagation();
              setZoomLevel(prev => prev === 1 ? 2 : 1);
            }}
          >
            <img
              src={productImages[activeImageIndex] || productImages[0]}
              alt={quickViewProduct.name || 'Jewellery'}
              style={{
                maxWidth: '92vw',
                maxHeight: '74vh',
                objectFit: 'contain',
                borderRadius: '8px',
                transform: `scale(${zoomLevel})`,
                transition: 'transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1)',
                boxShadow: '0 20px 50px rgba(0,0,0,0.9)'
              }}
              draggable={false}
            />
          </div>

          {/* Lightbox Bottom Strip: Angles Thumbnails & Hint */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.6rem',
              zIndex: 10
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {productImages.length > 1 && (
              <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', padding: '0.2rem' }}>
                {productImages.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setActiveImageIndex(idx);
                      setZoomLevel(1);
                    }}
                    style={{
                      width: '54px',
                      height: '54px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: activeImageIndex === idx ? '2px solid var(--gold-400)' : '1px solid rgba(255, 255, 255, 0.2)',
                      background: '#0a0a10',
                      cursor: 'pointer',
                      padding: 0,
                      opacity: activeImageIndex === idx ? 1 : 0.6,
                      transform: activeImageIndex === idx ? 'scale(1.08)' : 'scale(1)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <img src={img} alt={`View ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </button>
                ))}
              </div>
            )}
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.72rem', margin: 0 }}>
              💡 Double-click image to zoom in/out • Press <kbd style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 5px', borderRadius: '4px' }}>Esc</kbd> or click outside to close
            </p>
          </div>
        </div>
      )}

      <style>{`
        .kisna-modal {
          animation: modalPop 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        @keyframes modalPop {
          from {
            opacity: 0;
            transform: scale(0.96) translateY(12px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .related-prod-card:hover {
          transform: translateY(-3px);
          border-color: var(--gold-400) !important;
        }

        .kisna-add-cart-btn:hover {
          transform: translateY(-1px);
          filter: brightness(1.15);
          box-shadow: 0 6px 20px rgba(107, 33, 168, 0.6) !important;
        }

        @media (max-width: 768px) {
          .kisna-modal {
            max-height: 96vh !important;
            border-radius: 14px !important;
            margin: 0 !important;
          }
          .quickview-grid {
            grid-template-columns: 1fr !important;
            gap: 1.2rem !important;
          }
          .quickview-gallery-col {
            position: relative !important;
          }
          .kisna-scroll-content {
            padding: 0.8rem !important;
            padding-bottom: 5.5rem !important;
          }
        }
      `}</style>
    </div>
  );
}
